import React, { Fragment, useContext, useCallback } from 'react'
import { Trade, Token, ETHER, ChainId } from '@uniswap/sdk'
import styled from 'styled-components'
import { AutoColumn } from '../Column'
import { Text } from 'rebass'
import { ChevronRight, X } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { ThemeContext } from 'styled-components'
import CurrencyLogo from '../CurrencyLogo'
import { useSelectedTokenList } from '../../state/lists/hooks'
import Modal from '../Modal'
import { computeTradePriceBreakdown } from '../../utils/prices'
import { useTokenTaxDetection } from '../../hooks/useTaxTokenDetection'
import { useActiveWeb3React } from '../../hooks'

const ModalContent = styled.div`
  background: ${({ theme }) => theme.bg1};
  border-radius: 20px;
  padding: 24px 12px;
  max-width: 480px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
`

const RouteOption = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 16px 10px;
  border-radius: 12px;
  background: ${({ theme, isSelected }) => (isSelected ? theme.bg3 : theme.bg2)};
  color: ${({ theme }) => theme.text1};
  cursor: pointer;
  margin-bottom: 8px;
  transition: all 0.15s ease;
  border: 2px solid ${({ theme, isSelected }) => (isSelected ? theme.primary1 + 'b0' : 'transparent')};
  position: relative;

  &:hover {
    background: ${({ theme, isSelected }) => (isSelected ? theme.bg3 : theme.bg3)};
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const RoutePath = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 14px;
  font-weight: ${({ isSelected }) => (isSelected ? '600' : '500')};
  margin-bottom: 8px;
`

const RouteInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  position: relative;
  padding: 0 5px;
  margin-top: 6px;
`

const RecommendedBadge = styled.div<{ isSelected: boolean }>`
  position: absolute;
  top: 3px;
  right: 3px;
  background: ${({ theme, isSelected }) => (isSelected ? theme.primary1 : theme.green1)};
  color: white;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  opacity: ${({ isSelected }) => (isSelected ? 1 : 0.9)};
  transition: all 0.15s ease;
  z-index: 1;
`

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.text2};
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.bg2};
    color: ${({ theme }) => theme.text1};
  }
`

interface RouteSelectionModalProps {
  isOpen: boolean
  onDismiss: () => void
  trades: Trade[]
  selectedTrade: Trade | null
  onTradeSelect: (trade: Trade) => void
}

export default function RouteSelectionModal({
  isOpen,
  onDismiss,
  trades,
  selectedTrade,
  onTradeSelect
}: RouteSelectionModalProps) {
  const { t } = useTranslation()
  const theme = useContext(ThemeContext)
  const tokenList = useSelectedTokenList()
  const { chainId } = useActiveWeb3React()

  // 根据链ID获取原生代币符号
  const getNativeCurrencySymbol = useCallback(() => {
    if (chainId === ChainId.BSC) {
      return 'BNB'
    }
    return 'K'
  }, [chainId])

  // 检测输出代币的税费
  const outputToken = trades[0]?.outputAmount.currency
  const outputTaxInfo = useTokenTaxDetection(outputToken instanceof Token ? outputToken : undefined)

  const formatRoutePath = useCallback(
    (trade: Trade) => {
      const path = trade.route.path
      return path.map((token, i) => {
        const isLastItem: boolean = i === path.length - 1

        // 如果是最后一个代币且用户选择的是原生代币，显示原生代币
        if (isLastItem && trade.originalOutputCurrency === ETHER) {
          return (
            <Fragment key={`ETHER-${i}`}>
              <div style={{ display: 'flex', alignItems: 'center', margin: '0 3px' }}>
                <CurrencyLogo currency={ETHER} size="1.4rem" />
                <Text fontSize={13} fontWeight={500} ml="6px" color={theme.text1}>
                  {getNativeCurrencySymbol()}
                </Text>
              </div>
            </Fragment>
          )
        }

        // 尝试从 token list 中获取带有图标信息的代币
        const tokenWithLogo = tokenList[token.chainId]?.[token.address]
        const displayToken = tokenWithLogo || token

        return (
          <Fragment key={`${token.address}-${i}`}>
            <div style={{ display: 'flex', alignItems: 'center', margin: '0 3px' }}>
              <CurrencyLogo currency={displayToken} size="1.4rem" />
              <Text fontSize={13} fontWeight={500} ml="6px" color={theme.text1}>
                {displayToken.symbol}
              </Text>
            </div>
            {isLastItem ? null : <ChevronRight size={16} color={theme.text2} style={{ margin: '0 3px' }} />}
          </Fragment>
        )
      })
    },
    [tokenList, theme.text1, theme.text2, getNativeCurrencySymbol]
  )

  const formatOutputAmount = (trade: Trade) => {
    return trade.outputAmount.toSignificant(6)
  }

  // 计算考虑税费后的有效输出金额
  const getEffectiveOutputAmount = (trade: Trade) => {
    let outputValue = parseFloat(trade.outputAmount.toSignificant(6))
    if (outputTaxInfo.hasTax && outputTaxInfo.totalTax) {
      const taxMultiplier = 1 - outputTaxInfo.totalTax / 100
      outputValue = outputValue * taxMultiplier
    }
    return outputValue.toFixed(6)
  }

  const formatPriceImpact = useCallback((trade: Trade) => {
    const { priceImpactWithoutFee } = computeTradePriceBreakdown(trade)
    const impact = priceImpactWithoutFee || trade.priceImpact
    const impactPercent = impact.toFixed(2)
    const color = impact.lessThan('5') ? '#27ae60' : impact.lessThan('10') ? '#f39c12' : '#e74c3c'
    return { text: `${impactPercent}%`, color }
  }, [])

  const handleTradeSelect = (trade: Trade) => {
    onTradeSelect(trade)
    onDismiss()
  }

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxHeight={80}>
      <ModalContent>
        <CloseButton onClick={onDismiss}>
          <X size={20} />
        </CloseButton>

        <AutoColumn gap="16px">
          <Text fontWeight={600} fontSize={18} textAlign="center" color={theme.text1}>
            {t('Select Route')} ({trades.length})
          </Text>

          <AutoColumn gap="8px">
            {trades.map((trade, index) => {
              try {
                // 选中逻辑：
                // 1. 如果selectedTrade存在，检查当前trade是否与selectedTrade匹配
                // 2. 如果selectedTrade为null，默认选中第一个路由（最优路由）
                const isSelected = selectedTrade
                  ? selectedTrade.route.path.map(t => t.address).join('->') ===
                    trade.route.path.map(t => t.address).join('->')
                  : index === 0
                const priceImpact = formatPriceImpact(trade)
                const effectiveOutput = getEffectiveOutputAmount(trade)

                return (
                  <RouteOption key={index} isSelected={isSelected} onClick={() => handleTradeSelect(trade)}>
                    {index === 0 && <RecommendedBadge isSelected={isSelected}>{t('Best')}</RecommendedBadge>}

                    <RoutePath isSelected={isSelected}>{formatRoutePath(trade)}</RoutePath>
                    <RouteInfo>
                      <div style={{ color: priceImpact.color, fontWeight: 500 }}>
                        {t('Impact')}: {priceImpact.text}
                      </div>
                      <div style={{ fontWeight: 500 }}>
                        {t('Output')}: {outputTaxInfo.hasTax ? effectiveOutput : formatOutputAmount(trade)}
                      </div>
                    </RouteInfo>
                  </RouteOption>
                )
              } catch (error) {
                console.error('Error rendering route option:', error)
                return null
              }
            })}
          </AutoColumn>
        </AutoColumn>
      </ModalContent>
    </Modal>
  )
}
